import axios, { AxiosInstance } from 'axios'

/**
 * Deux backends, un seul portail.
 *
 * Le mode TEST et le mode LIVE ne sont pas deux jeux de données dans une même
 * base : ce sont deux environnements Youdégo distincts. Le portail parle donc
 * à l'un ou à l'autre selon le mode sélectionné —
 *   LIVE → prod  (clés yk_live_, vraies courses)
 *   TEST → pprod (clés yk_test_, simulateur sandbox)
 *
 * La session (cookie httpOnly `partner_token`) est valable sur les deux, car
 * les comptes partenaires sont mirroités prod → pprod aux mêmes UUID et les
 * deux environnements signent avec le même PARTNER_PORTAL_JWT_SECRET.
 */

export type ApiMode = 'TEST' | 'LIVE'

export const LIVE_API_URL =
  process.env.VUE_APP_API_URL || 'https://api.youdego.app/api'
export const TEST_API_URL =
  process.env.VUE_APP_SANDBOX_API_URL || 'https://pprod-api.youdego.app/api'

function createClient(baseURL: string, ownsSession: boolean): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: true, // cookie httpOnly partner_token
    timeout: 60000,
  })

  client.interceptors.request.use((config) => {
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    } else {
      config.headers['Content-Type'] = 'application/json'
    }
    return config
  })

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Seul le backend qui porte la session peut nous déconnecter. Un 401
      // venant de l'AUTRE environnement veut dire « cet environnement ne me
      // reconnaît pas », pas « la session a expiré » : rediriger dans ce cas
      // provoquait une boucle, la page de connexion revalidant aussitôt la
      // session auprès du backend qui, lui, la reconnaît.
      if (ownsSession && error.response?.status === 401 && window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
  )

  return client
}

// La session est émise et validée par la production : c'est le seul client
// autorisé à provoquer une déconnexion.
const liveClient = createClient(LIVE_API_URL, true)
const testClient = createClient(TEST_API_URL, TEST_API_URL === LIVE_API_URL)

/**
 * Vrai quand l'environnement interrogé refuse la session alors que celle-ci
 * reste valide côté production — typiquement le sandbox joignable mais qui
 * n'a pas connaissance du compte.
 */
export function isEnvironmentUnauthorized(error: any): boolean {
  const status = error?.response?.status
  return status === 401 || status === 403
}

/** Client HTTP de l'environnement correspondant au mode donné. */
export function apiFor(mode: ApiMode): AxiosInstance {
  return mode === 'LIVE' ? liveClient : testClient
}

/** Base URL publique de l'API partenaire, affichée dans la documentation. */
export function apiBaseUrlFor(mode: ApiMode): string {
  return (mode === 'LIVE' ? LIVE_API_URL : TEST_API_URL) + '/v1'
}

/**
 * Client de production : sert à l'authentification, qui se fait toujours
 * contre la prod (source de vérité des comptes).
 */
export const authClient = liveClient

export default liveClient
